/**
 * The read behind a master set's slot grid.
 *
 * One call, `wish_set_slot_grid`, which returns the slots already scoped and
 * already resolved: every tile in scope with whether the person fills it.
 *
 * This used to be two reads - the whole slot list plus the whole owned list -
 * with the ownership rule re-derived in the client from both. That mirror is
 * gone. Two implementations of one rule drift, and the one in the client is the
 * one that ends up wrong, so the rule now lives only in
 * `public.wish_set_slot_rows` and the progress bar counts the same rows the grid
 * draws.
 */

import { supabase } from '../lib/supabase';

/**
 * The biggest set in the catalogue is Base Set at 408 slots, so a ceiling this
 * far above it can only be hit by something going wrong. It is checked rather
 * than paged because PostgREST does not honour a Range header on an RPC - a
 * paging loop here would re-read the same first page for ever - and a grid that
 * is quietly short of the count above it is worse than a warning in the log.
 */
const EXPECTED_CEILING = 1000;

/**
 * Every slot of a master set or sub set, with ownership resolved.
 *
 * @param {string} setId    digitalcards.sets.id
 * @param {object|null} scope  the sub set filter, null for the whole master
 * @param {string|null} asUserId  a child's users.id when a parent is looking at
 *   their list; null means the caller themself. The server allows nothing else.
 * @returns {Promise<Array<{ card_id: string, local_id: string, card_name: string,
 *   printing_subtype: string|null, printing_stamps: string[], owned: boolean,
 *   printing_unknown: boolean }>>}
 */
export async function fetchSlotGrid(setId, scope = null, asUserId = null) {
  if (!setId) return [];
  const { data, error } = await supabase.rpc('wish_set_slot_grid', {
    p_set_id: setId,
    p_scope: scope,
    p_as: asUserId,
  });
  if (error) throw error;
  const rows = data || [];
  if (rows.length >= EXPECTED_CEILING) {
    console.warn('[slot grid] %s returned %d slots, which is past anything the catalogue holds', setId, rows.length);
  }
  return rows;
}

/**
 * The same printing rows for a handful of cards from anywhere: a search
 * result, a chase binder, the flat catalogue's page. A printing is a card
 * everywhere, not only inside a set, and this is what lets every grid draw
 * the 1st Edition and the Shadowless beside the plain one.
 *
 * @param {string[]} cardIds catalogue ids
 * @param {string|null} [asUserId]
 */
export async function fetchSlotGridForCards(cardIds, asUserId = null) {
  const ids = [...new Set((cardIds || []).filter(Boolean))];
  if (!ids.length) return [];
  const { data, error } = await supabase.rpc('wish_cards_slot_grid', {
    p_card_ids: ids,
    p_as: asUserId,
  });
  if (error) throw error;
  return data || [];
}
